import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AddAccountingItemModal from '@modals/AddAccountingItemModal'
import CreateOrEditInvoiceItemModal from '@modals/CreateOrEditInvoiceItemModal'
import CreateOrEditInvoiceModal from '@modals/CreateOrEditInvoiceModal'

import type { CampaignModel, InvoiceModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showAddAccountingItemModal: false,
  showCreateOrEditInvoiceModal: false,
  showCreateOrEditInvoiceItemModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (
  component: keyof typeof componentCallbacks,
  setState: SetState,
) => {
  const componentCallbacks = {
    AddAccountingItemModal: {
      closeModal: () => setState({ showAddAccountingItemModal: false }),
    },
    CreateOrEditInvoiceItemModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceItemModal: false }),
      createInvoiceItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoiceItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditInvoiceModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

type EditInvoiceProps = {
  campaign: CampaignModel,
}

const EditInvoice = (props: EditInvoiceProps) => {
  const { campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showAddAccountingItemModal, showCreateOrEditInvoiceModal, showCreateOrEditInvoiceItemModal,
  } = state

  const { pageFilters } = useFilters()

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, baseModalKey, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showAddAccountingItemModal: (payload: { campaign?: CampaignModel }) => {
        setModalData('AddAccountingItemModal', payload)
        setState({ showAddAccountingItemModal: true })
      },
      showCreateOrEditInvoiceItemModal: (payload: { invoiceItem?: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceItemModal', payload)
        setState({ showCreateOrEditInvoiceItemModal: true })
      },
      showCreateOrEditInvoiceModal: (payload: { invoice: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceModal', payload)
        setState({ showCreateOrEditInvoiceModal: true })
      },
    },
  }), [baseModalKey, topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaign={campaign} pageFilters={pageFilters} />

        <AddAccountingItemModal
          callbacks={callbacks('AddAccountingItemModal', setState)}
          showModal={showAddAccountingItemModal}
        />

        <CreateOrEditInvoiceItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditInvoiceItemModal', setState)}
          showModal={showCreateOrEditInvoiceItemModal}
        />

        <CreateOrEditInvoiceModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditInvoiceModal', setState)}
          showModal={showCreateOrEditInvoiceModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default EditInvoice
